/**
 * SeedCrypt - Military-grade Seed Phrase Encryption
 * Features: AES-256-GCM, Argon2id KDF, LSB Steganography
 *
 * IMPORTANT: This tool encrypts and hides ONLY A PART of your seed phrase.
 * NEVER share your complete seed phrase with anyone or any service.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { Icons } from '../components/Icons';
import {
  encryptSeedPhrase,
  encryptSeedPhraseWithProgress,
  decryptSeedPhrase,
  getPassphraseStrength,
  validateSeedPhrase,
  exportEncryptedSeed,
  importEncryptedSeed,
  type EncryptionResult,
} from '../services/seedEncryption';
import {
  encodeIntoImage,
  decodeFromImage,
  loadImageFile,
  imageToPNGBlob,
  getFormatError,
  type StegoOptions,
} from '../services/steganography';

type TabType = 'encrypt' | 'decrypt' | 'steganography';

export function SeedCrypt() {
  const { t } = useTranslation();
   
  const { language: _lang } = useLanguage(); // Force re-render on language change

  const [activeTab, setActiveTab] = useState<TabType>('encrypt');
  const [showGuide, setShowGuide] = useState(false);

  return (
    <div className="seedvault-page">
      {/* Header */}
      <div className="seedvault-header">
        <div className="seedvault-header-content">
          <div className="seedvault-title-row">
            <div className="seedvault-icon">
              <Icons.Shield size={32} color="white" />
            </div>
            <div>
              <h1 className="seedvault-title">{t('seedVault.title')}</h1>
              <p className="seedvault-subtitle">{t('seedVault.subtitle')}</p>
            </div>
          </div>

          {/* Features */}
          <div className="seedvault-features">
            <div className="feature-card">
              <div className="feature-icon">
                <Icons.Lock size={20} color="white" />
              </div>
              <div className="feature-text">
                <h2>{t('seedVault.features.aes.title')}</h2>
                <p>{t('seedVault.features.aes.description')}</p>
              </div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Icons.Image size={20} color="white" />
              </div>
              <div className="feature-text">
                <h3>{t('seedVault.features.steganography.title')}</h3>
                <p>{t('seedVault.features.steganography.description')}</p>
              </div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Icons.Shield size={20} color="white" />
              </div>
              <div className="feature-text">
                <h3>{t('seedVault.features.zeroKnowledge.title')}</h3>
                <p>{t('seedVault.features.zeroKnowledge.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CRITICAL WARNING - Never share seed */}
      <div style={{
        margin: '0 auto 20px',
        maxWidth: '900px',
        padding: '16px 20px',
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)',
        border: '2px solid #ef4444',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px'
      }}>
        <div style={{
          background: '#ef4444',
          borderRadius: '50%',
          padding: '8px',
          flexShrink: 0
        }}>
          <Icons.AlertTriangle size={24} color="white" />
        </div>
        <div>
          <h3 style={{ color: '#ef4444', fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>
            {t('seedVault.criticalWarning.title')}
          </h3>
          <p style={{ color: 'var(--text)', fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' }}>
            {t('seedVault.criticalWarning.description').split(t('seedVault.criticalWarning.scam'))[0]}
            <strong style={{ color: '#ef4444' }}>{t('seedVault.criticalWarning.scam')}</strong>.
          </p>
          <p style={{ color: 'var(--muted)', fontSize: '13px', lineHeight: '1.5' }}>
            {t('seedVault.criticalWarning.toolDescription')}
          </p>
        </div>
      </div>

      {/* User Guide (collapsible) */}
      <div className="user-guide">
        <button className="user-guide-toggle" onClick={() => setShowGuide(!showGuide)}>
          <div className="user-guide-toggle-left">
            <div className="user-guide-icon">
              <Icons.BookOpen size={24} color="white" />
            </div>
            <div className="user-guide-text">
              <h3>{t('seedVault.userGuide.title')}</h3>
              <p>{t('seedVault.userGuide.description')}</p>
            </div>
          </div>
          <div className={`user-guide-chevron ${showGuide ? 'open' : ''}`}>
            <Icons.ChevronDown size={24} />
          </div>
        </button>

        {showGuide && (
          <div className="user-guide-content open">
            {/* How it works */}
            <h4 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>{t('seedVault.userGuide.howItWorks')}</h4>
            <ol style={{ paddingLeft: '20px', lineHeight: '1.8', marginBottom: '16px' }}>
              <li>{t('seedVault.userGuide.step1')}</li>
              <li>{t('seedVault.userGuide.step2')}</li>
              <li>{t('seedVault.userGuide.step3')}</li>
            </ol>
            <div style={{ marginBottom: '20px', padding: '12px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fbbf24' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#78350f' }}>
                {t('seedVault.userGuide.important')}
              </p>
            </div>

            {/* Security Tips */}
            <div style={{ marginBottom: '20px', padding: '16px', background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderRadius: '10px', border: '1px solid #3b82f6' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#1e3a8a', marginBottom: '8px' }}>{t('seedVault.securityTip.title')}</h4>
              <p style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.6', marginBottom: '8px' }}>{t('seedVault.securityTip.description')}</p>
              <p style={{ fontSize: '12px', color: '#3730a3', lineHeight: '1.5', fontStyle: 'italic' }}>{t('seedVault.securityTip.reason')}</p>
            </div>

            {/* Use Case - Travelers */}
            <div style={{ padding: '16px', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(99, 102, 241, 0.05) 100%)', borderRadius: '10px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
              <h4 style={{ color: '#3b82f6', fontSize: '14px', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {t('seedVault.userGuide.useCaseTitle')}
              </h4>
              <p style={{ color: 'var(--text)', fontSize: '13px', lineHeight: '1.6', marginBottom: '12px' }}>
                {t('seedVault.userGuide.useCaseIntro')}
              </p>
              <ul style={{ color: 'var(--text)', fontSize: '13px', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '12px' }}>
                <li>{t('seedVault.userGuide.useCaseTip1')}</li>
                <li>{t('seedVault.userGuide.useCaseTip2')}</li>
                <li>{t('seedVault.userGuide.useCaseTip3')}</li>
                <li>{t('seedVault.userGuide.useCaseTip4')}</li>
                <li>{t('seedVault.userGuide.useCaseTip5')}</li>
              </ul>
              <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                <p style={{ color: '#10b981', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>{t('seedVault.userGuide.exampleTitle')}</p>
                <p style={{ color: 'var(--text)', fontSize: '12px', lineHeight: '1.6' }}>{t('seedVault.userGuide.exampleDesc')}</p>
              </div>
              <p style={{ color: 'var(--muted)', fontSize: '12px', fontStyle: 'italic', marginTop: '12px' }}>{t('seedVault.userGuide.proTip')}</p>
            </div>

            {/* 100% Client-Side Notice */}
            <div style={{ marginTop: '20px', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#065f46' }}>
                <strong>{t('seedVault.securityNotice.title')}</strong> {t('seedVault.securityNotice.description')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-nav">
          <button
            className={`tab-button ${activeTab === 'encrypt' ? 'active' : ''}`}
            onClick={() => setActiveTab('encrypt')}
          >
            <Icons.Lock size={20} />
            <span>{t('seedVault.tabs.encrypt')}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'decrypt' ? 'active' : ''}`}
            onClick={() => setActiveTab('decrypt')}
          >
            <Icons.Unlock size={20} />
            <span>{t('seedVault.tabs.decrypt')}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'steganography' ? 'active' : ''}`}
            onClick={() => setActiveTab('steganography')}
          >
            <Icons.Image size={20} />
            <span>{t('seedVault.tabs.steganography')}</span>
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'encrypt' && <EncryptTab />}
          {activeTab === 'decrypt' && <DecryptTab />}
          {activeTab === 'steganography' && <SteganographyTab />}
        </div>
      </div>
    </div>
  );
}

/**
 * Encrypt Tab
 */
function EncryptTab() {
  const { t } = useTranslation();
  const [seedPhrase, setSeedPhrase] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [label, setLabel] = useState('');
  const [recoveryHint, setRecoveryHint] = useState('');
  const [algorithm, setAlgorithm] = useState<'AES-256-GCM' | 'XChaCha20-Poly1305'>('AES-256-GCM');
  const [showSeed, setShowSeed] = useState(false);
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [isEncrypting, setIsEncrypting] = useState(false);
  const [encryptionProgress, setEncryptionProgress] = useState(0);
  const [encryptedResult, setEncryptedResult] = useState<EncryptionResult | null>(null);
  const [error, setError] = useState('');
  const [testingDecryption, setTestingDecryption] = useState(false);
  const [testSuccess, setTestSuccess] = useState<boolean | null>(null);
  const [originalSeedForTest, setOriginalSeedForTest] = useState('');

  const seedValidation = useMemo(() => validateSeedPhrase(seedPhrase), [seedPhrase]);
  const passphraseStrength = useMemo(() => getPassphraseStrength(passphrase), [passphrase]);
  const passphrasesMatch = passphrase === confirmPassphrase && passphrase.length > 0;
  const canEncrypt = seedValidation.isValid && passphrase.length >= 8 && passphrasesMatch && !isEncrypting;

  const handleEncrypt = async () => {
    if (!canEncrypt) return;
    setIsEncrypting(true);
    setError('');
    setEncryptionProgress(0);
    setTestSuccess(null);

    try {
      // Save original seed for testing
      setOriginalSeedForTest(seedPhrase);

      // Encrypt with real-time progress updates from Web Worker
      const result = await encryptSeedPhraseWithProgress(
        seedPhrase,
        passphrase,
        algorithm,
        recoveryHint || undefined,
        (progress) => {
          // Update progress bar with real progress from worker
          setEncryptionProgress(Math.round(progress));
        }
      );

      setEncryptedResult(result);
      setSeedPhrase('');
      setConfirmPassphrase('');
      // Keep passphrase for immediate test
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Encryption failed');
      setEncryptionProgress(0);
    } finally {
      setIsEncrypting(false);
    }
  };

  const handleTestDecryption = async () => {
    if (!encryptedResult || !passphrase || !originalSeedForTest) return;
    setTestingDecryption(true);
    setError('');

    try {
      const decrypted = await decryptSeedPhrase(encryptedResult, passphrase);
      const success = decrypted === originalSeedForTest;
      setTestSuccess(success);
      if (!success) {
        setError('Test failed: Decrypted data does not match original!');
      }
    } catch (err) {
      setTestSuccess(false);
      setError('Test failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setTestingDecryption(false);
    }
  };

  const handleDownloadQR = async () => {
    if (!encryptedResult) return;
    try {
      const QRCode = (await import('qrcode')).default;
      const qrDataUrl = await QRCode.toDataURL(JSON.stringify(encryptedResult), {
        width: 512,
        margin: 2,
        errorCorrectionLevel: 'H',
      });

      const a = document.createElement('a');
      a.href = qrDataUrl;
      a.download = `seed-vault-qr-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      setError('QR Code generation failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleDownloadJSON = () => {
    if (!encryptedResult) return;
    const blob = exportEncryptedSeed(encryptedResult, label || 'My Seed Vault');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seed-vault-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyEncrypted = () => {
    if (!encryptedResult) return;
    navigator.clipboard.writeText(JSON.stringify(encryptedResult, null, 2));
  };

  const handleReset = () => {
    setEncryptedResult(null);
    setError('');
  };

  if (encryptedResult) {
    return (
      <div>
        <div className="success-card">
          <div className="success-header">
            <div className="success-icon">
              <Icons.Check size={28} color="white" />
            </div>
            <div className="success-text">
              <h3>{t('seedVault.success.encrypted')}</h3>
              <p>{t('seedVault.success.encryptedWith', { algorithm: encryptedResult.algorithm })}</p>
            </div>
          </div>
          <div style={{ marginTop: '16px', padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #d1d5db' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '8px' }}>{t('seedVault.success.truncated')}</div>
            <div style={{ fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all', background: '#f3f4f6', padding: '12px', borderRadius: '6px' }}>
              {encryptedResult.ciphertext.substring(0, 100)}...
            </div>
            {encryptedResult.recoveryHint && (
              <div style={{ marginTop: '12px', padding: '12px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '6px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#78350f', marginBottom: '4px' }}>{t('seedVault.success.recoveryHint')}</div>
                <div style={{ fontSize: '13px', color: '#92400e' }}>{encryptedResult.recoveryHint}</div>
              </div>
            )}
          </div>
        </div>

        {/* Test Decryption Section */}
        {passphrase && originalSeedForTest && testSuccess === null && (
          <div style={{ marginTop: '20px', padding: '16px', background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', border: '2px solid #3b82f6', borderRadius: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                🧪
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1e3a8a' }}>{t('seedVault.test.title')}</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#1e40af' }}>
                  {t('seedVault.test.description')}
                </p>
              </div>
            </div>
            <button
              className="btn-primary"
              onClick={handleTestDecryption}
              disabled={testingDecryption}
              style={{ width: '100%' }}
            >
              {testingDecryption ? (
                <>
                  <div className="spinner" />
                  <span>{t('seedVault.test.testing')}</span>
                </>
              ) : (
                <>
                  <Icons.Check size={20} />
                  <span>{t('seedVault.test.button')}</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Test Result */}
        {testSuccess === true && (
          <div style={{ marginTop: '16px', padding: '16px', background: '#d1fae5', border: '2px solid #10b981', borderRadius: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#065f46' }}>
              <Icons.Check size={20} />
              <strong>{t('seedVault.test.success')}</strong>
            </div>
            <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#065f46' }}>
              {t('seedVault.test.successDesc')}
            </p>
          </div>
        )}
        {testSuccess === false && (
          <div style={{ marginTop: '16px', padding: '16px', background: '#fee', border: '2px solid #ef4444', borderRadius: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#991b1b' }}>
              <Icons.AlertTriangle size={20} />
              <strong>{t('seedVault.test.failed')}</strong>
            </div>
            <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#991b1b' }}>
              {t('seedVault.test.failedDesc')}
            </p>
          </div>
        )}

        <div className="button-grid" style={{ marginTop: '20px' }}>
          <button className="btn-primary" onClick={handleDownloadJSON}>
            <Icons.Download size={20} />
            {t('seedVault.buttons.downloadJson')}
          </button>
          <button className="btn-secondary" onClick={handleDownloadQR}>
            <Icons.Image size={20} />
            {t('seedVault.buttons.qrCode')}
          </button>
          <button className="btn-secondary" onClick={handleCopyEncrypted}>
            <Icons.Copy size={20} />
            {t('seedVault.buttons.copy')}
          </button>
        </div>

        <button className="btn-secondary" onClick={handleReset} style={{ marginTop: '16px', width: '100%' }}>
          {t('seedVault.buttons.encryptAnother')}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Workflow Info */}
      <div className="workflow-info">
        <div className="workflow-info-icon">1</div>
        <div className="workflow-info-content">
          <h3>{t('seedVault.encrypt.step')}</h3>
          <p>{t('seedVault.encrypt.stepDesc')}</p>
        </div>
      </div>

      {/* Seed Phrase Input */}
      <div style={{ marginBottom: '20px' }}>
        <label className="form-label">{t('seedVault.encrypt.textLabel')}</label>
        <div className="input-wrapper">
          <textarea
            className="form-textarea"
            value={seedPhrase}
            onChange={(e) => setSeedPhrase(e.target.value)}
            placeholder={t('seedVault.encrypt.textPlaceholder')}
            rows={4}
            style={{ filter: !showSeed ? 'blur(4px)' : 'none', transition: 'filter 0.2s' }}
          />
          <button className="input-icon-button" onClick={() => setShowSeed(!showSeed)}>
            {showSeed ? <Icons.EyeOff size={20} /> : <Icons.Eye size={20} />}
          </button>
        </div>
        {seedPhrase && !seedValidation.isValid && (
          <div style={{ marginTop: '8px', padding: '12px', background: '#fee', border: '1px solid #fca5a5', borderRadius: '8px' }}>
            {seedValidation.errors.map((err, i) => (
              <div key={i} style={{ fontSize: '13px', color: '#991b1b' }}>• {err}</div>
            ))}
          </div>
        )}
        {seedValidation.isValid && (
          <div style={{ marginTop: '8px', padding: '12px', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', fontSize: '13px', color: '#065f46' }}>
            ✓ Phrase valide de {seedValidation.wordCount} mots
          </div>
        )}
      </div>

      {/* Passphrase */}
      <div style={{ marginBottom: '20px' }}>
        <label className="form-label">Phrase de passe de chiffrement (min 8 caractères)</label>
        <div className="input-wrapper">
          <input
            type={showPassphrase ? 'text' : 'password'}
            className="form-input"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="Entrez une phrase de passe forte"
          />
          <button className="input-icon-button" onClick={() => setShowPassphrase(!showPassphrase)}>
            {showPassphrase ? <Icons.EyeOff size={20} /> : <Icons.Eye size={20} />}
          </button>
        </div>

        {passphrase && (
          <div style={{ marginTop: '12px', padding: '16px', background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: passphraseStrength.color }}>{passphraseStrength.label}</span>
              <span style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>{passphrase.length} caractères</span>
            </div>
            <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(passphraseStrength.score / 4) * 100}%`, background: passphraseStrength.color, transition: 'width 0.3s' }} />
            </div>
            {passphraseStrength.suggestions.length > 0 && (
              <div style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280' }}>
                {passphraseStrength.suggestions.map((suggestion, i) => (
                  <div key={i}>• {suggestion}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm Passphrase */}
      <div style={{ marginBottom: '20px' }}>
        <label className="form-label">Confirmer la phrase de passe</label>
        <input
          type={showPassphrase ? 'text' : 'password'}
          className="form-input"
          value={confirmPassphrase}
          onChange={(e) => setConfirmPassphrase(e.target.value)}
          placeholder="Ressaisir la phrase de passe"
          style={{ borderColor: confirmPassphrase && !passphrasesMatch ? '#ef4444' : undefined }}
        />
        {confirmPassphrase && passphrasesMatch && (
          <div style={{ marginTop: '8px', fontSize: '13px', color: '#10b981' }}>✓ Les phrases de passe correspondent</div>
        )}
        {confirmPassphrase && !passphrasesMatch && (
          <div style={{ marginTop: '8px', fontSize: '13px', color: '#ef4444' }}>✗ Les phrases de passe ne correspondent pas</div>
        )}
      </div>

      {/* Algorithm Selection */}
      <div style={{ marginBottom: '20px' }}>
        <label className="form-label">Algorithme de chiffrement</label>
        <select
          aria-label="Algorithme de chiffrement"
          className="form-select"
          value={algorithm}
          onChange={(e) => setAlgorithm(e.target.value as 'AES-256-GCM' | 'XChaCha20-Poly1305')}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: '10px',
            border: '1px solid #d1d5db',
            fontSize: '14px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          <option value="AES-256-GCM">AES-256-GCM (Standard, accélération matérielle sur desktop)</option>
          <option value="XChaCha20-Poly1305">XChaCha20-Poly1305 (Plus rapide sur mobile, IV étendu 192-bit)</option>
        </select>
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
          {algorithm === 'AES-256-GCM' ? (
            <span>✓ Algorithme standard avec accélération matérielle sur processeurs modernes</span>
          ) : (
            <span>✓ Optimisé pour appareils mobiles, IV de 192-bit pour sécurité maximale</span>
          )}
        </div>
      </div>

      {/* Label (Optional) */}
      <div style={{ marginBottom: '20px' }}>
        <label className="form-label">Étiquette (Optionnel)</label>
        <input
          type="text"
          className="form-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Ex : Portefeuille principal, Cold Storage, etc."
        />
      </div>

      {/* Recovery Hint (Optional) */}
      <div style={{ marginBottom: '20px' }}>
        <label className="form-label">Indice de récupération (Optionnel)</label>
        <input
          type="text"
          className="form-input"
          value={recoveryHint}
          onChange={(e) => setRecoveryHint(e.target.value)}
          placeholder="Ex : Prénom de ma mère + année de naissance"
        />
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
          💡 Un indice pour vous rappeler votre passphrase (ne donnez jamais la passphrase complète !)
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ marginBottom: '20px', padding: '16px', background: '#fee', border: '2px solid #fca5a5', borderRadius: '10px', color: '#991b1b' }}>
          <strong>Erreur :</strong> {error}
        </div>
      )}

      {/* Progress Bar */}
      {isEncrypting && (
        <div style={{ marginBottom: '20px', padding: '16px', background: 'white', border: '2px solid #667eea', borderRadius: '10px' }}>
          <div style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 600, color: '#667eea' }}>
            Chiffrement en cours...
          </div>
          <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${encryptionProgress}%`,
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
            {encryptionProgress < 40 && '📝 Dérivation de clé (Argon2id)...'}
            {encryptionProgress >= 40 && encryptionProgress < 80 && '🔐 Chiffrement des données...'}
            {encryptionProgress >= 80 && '✅ Finalisation...'}
          </div>
        </div>
      )}

      {/* Encrypt Button */}
      <button className="btn-primary" onClick={handleEncrypt} disabled={!canEncrypt}>
        {isEncrypting ? (
          <>
            <div className="spinner" />
            <span>Chiffrement en cours...</span>
          </>
        ) : (
          <>
            <Icons.Lock size={22} />
            <span>Chiffrer la phrase</span>
          </>
        )}
      </button>
    </div>
  );
}

/**
 * Decrypt Tab
 */
function DecryptTab() {
  const [encryptedData, setEncryptedData] = useState<EncryptionResult | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [decryptedSeed, setDecryptedSeed] = useState('');
  const [showDecrypted, setShowDecrypted] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await importEncryptedSeed(file);
      setEncryptedData(data);
      setError('');
    } catch {
      setError('Failed to parse file. Please upload a valid encrypted seed file.');
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setError('Please drop a valid JSON file');
      return;
    }

    try {
      const data = await importEncryptedSeed(file);
      setEncryptedData(data);
      setError('');
    } catch {
      setError('Failed to parse file. Please upload a valid encrypted seed file.');
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handlePasteJSON = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (!text) {
      setEncryptedData(null);
      return;
    }

    try {
      const data = JSON.parse(text);
      if (data.ciphertext && data.salt && data.iv && data.tag) {
        setEncryptedData(data as EncryptionResult);
        setError('');
      }
    } catch {
      setError('Invalid JSON format');
    }
  };

  const handleDecrypt = async () => {
    if (!encryptedData || !passphrase) return;
    setIsDecrypting(true);
    setError('');

    try {
      const seed = await decryptSeedPhrase(encryptedData, passphrase);
      setDecryptedSeed(seed);
      setPassphrase('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Decryption failed');
    } finally {
      setIsDecrypting(false);
    }
  };

  const handleReset = () => {
    setEncryptedData(null);
    setPassphrase('');
    setDecryptedSeed('');
    setError('');
    setShowDecrypted(false);
  };

  if (decryptedSeed) {
    return (
      <div>
        <div className="success-card">
          <div className="success-header">
            <div className="success-icon">
              <Icons.Check size={28} color="white" />
            </div>
            <div className="success-text">
              <h3>Déchiffrement réussi !</h3>
              <p>Votre phrase de récupération a été récupérée</p>
            </div>
          </div>
          <div style={{ marginTop: '16px', padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #d1d5db' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>Votre phrase :</div>
              <button onClick={() => setShowDecrypted(!showDecrypted)} style={{ fontSize: '12px', color: '#667eea', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                {showDecrypted ? 'Masquer' : 'Afficher'}
              </button>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all', background: '#f3f4f6', padding: '12px', borderRadius: '6px', filter: !showDecrypted ? 'blur(8px)' : 'none' }}>
              {decryptedSeed}
            </div>
          </div>
        </div>

        <div className="button-grid">
          <button className="btn-primary" onClick={() => navigator.clipboard.writeText(decryptedSeed)}>
            <Icons.Copy size={20} />
            Copier la phrase
          </button>
          <button className="btn-secondary" onClick={handleReset}>
            <Icons.RotateCcw size={20} />
            Déchiffrer une autre
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Workflow Info */}
      <div className="workflow-info" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderColor: '#3b82f6' }}>
        <div className="workflow-info-icon" style={{ background: '#3b82f6' }}>2</div>
        <div className="workflow-info-content">
          <h3 style={{ color: '#1e3a8a' }}>Étape 2 : Déchiffrer votre texte</h3>
          <p style={{ color: '#1e40af' }}>
            Téléversez le fichier JSON que vous avez reçu de l'Étape 1 (onglet Chiffrer) ou collez les données JSON chiffrées.
            Entrez votre phrase de passe pour récupérer votre texte original.
          </p>
        </div>
      </div>

      {/* Upload */}
      <div style={{ marginBottom: '20px' }}>
        <label className="form-label">Importer les données chiffrées</label>
        <div
          className="upload-zone"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{
            cursor: 'pointer',
            border: isDragging ? '3px dashed #667eea' : '2px dashed #d1d5db',
            background: isDragging ? '#f0f4ff' : 'white',
            transition: 'all 0.2s',
          }}
          onClick={() => document.getElementById('file-upload')?.click()}
        >
          <div className="upload-icon">
            <Icons.Upload size={24} style={{ color: isDragging ? '#667eea' : '#9ca3af' }} />
          </div>
          <div className="upload-text" style={{ color: isDragging ? '#667eea' : undefined }}>
            {isDragging ? '📂 Déposez le fichier ici' : 'Téléverser le fichier JSON'}
          </div>
          <div className="upload-hint">Cliquer pour parcourir ou glisser-déposer</div>
          <input id="file-upload" type="file" accept=".json" onChange={handleFileUpload} hidden />
        </div>

        <div style={{ textAlign: 'center', margin: '16px 0', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>OU</div>

        <textarea
          className="form-textarea"
          onChange={handlePasteJSON}
          placeholder='Collez les données JSON chiffrées ici...'
          rows={6}
        />

        {encryptedData && (
          <div style={{ marginTop: '8px', padding: '12px', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', fontSize: '13px', color: '#065f46' }}>
            ✓ Données chiffrées valides chargées
          </div>
        )}
      </div>

      {/* Passphrase */}
      {encryptedData && (
        <div style={{ marginBottom: '20px' }}>
          <label className="form-label">Phrase de passe de déchiffrement</label>
          <div className="input-wrapper">
            <input
              type={showPassphrase ? 'text' : 'password'}
              className="form-input"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="Entrez votre phrase de passe"
              onKeyDown={(e) => { if (e.key === 'Enter' && passphrase) handleDecrypt(); }}
            />
            <button className="input-icon-button" onClick={() => setShowPassphrase(!showPassphrase)}>
              {showPassphrase ? <Icons.EyeOff size={20} /> : <Icons.Eye size={20} />}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ marginBottom: '20px', padding: '16px', background: '#fee', border: '2px solid #fca5a5', borderRadius: '10px', color: '#991b1b' }}>
          <strong>Erreur :</strong> {error}
        </div>
      )}

      {/* Decrypt Button */}
      {encryptedData && (
        <button className="btn-primary" onClick={handleDecrypt} disabled={!passphrase || isDecrypting}>
          {isDecrypting ? (
            <>
              <div className="spinner" />
              <span>Déchiffrement en cours...</span>
            </>
          ) : (
            <>
              <Icons.Unlock size={22} />
              <span>Déchiffrer la phrase</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Steganography Tab
 */
function SteganographyTab() {
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [encryptedData, setEncryptedData] = useState('');
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [imageCapacity, setImageCapacity] = useState<{ bytes: number; kb: number } | null>(null);
  const [pattern, setPattern] = useState<'sequential' | 'random' | 'spread'>('sequential');
  const [patternPassword, setPatternPassword] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [resultImageUrl, setResultImageUrl] = useState<string>('');
  const [extractedData, setExtractedData] = useState<string>('');
  const [error, setError] = useState('');

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formatError = getFormatError(file);
    if (formatError) {
      setError(formatError);
      return;
    }

    setCoverImage(file);
    setError('');

    // Calculate image capacity
    try {
      const imageData = await loadImageFile(file);
      const pixels = imageData.width * imageData.height;
      const bitsAvailable = pixels * 3 * 1; // RGB channels, 1 bit per channel
      const bytesAvailable = Math.floor(bitsAvailable / 8);
      const overhead = 100; // Reserve 100 bytes for metadata
      const capacityBytes = bytesAvailable - overhead;
      const capacityKB = (capacityBytes / 1024);

      setImageCapacity({ bytes: capacityBytes, kb: capacityKB });
    } catch (err) {
      console.error('Failed to calculate image capacity:', err);
      setImageCapacity(null);
    }
  };

  const handleEncode = async () => {
    if (!coverImage || !encryptedData) return;
    setIsProcessing(true);
    setError('');

    try {
      const encrypted = JSON.parse(encryptedData) as EncryptionResult;
      const dataStr = JSON.stringify(encrypted);
      const dataBytes = new TextEncoder().encode(dataStr);
      const imageData = await loadImageFile(coverImage);

      const options: StegoOptions = {
        pattern,
        bitsPerPixel: 1,
        channels: ['R', 'G', 'B'],
        seed: pattern === 'random' ? patternPassword : undefined,
      };

      const stegoResult = encodeIntoImage(imageData, dataBytes, options);
      const blob = await imageToPNGBlob(stegoResult.imageData);
      const url = URL.createObjectURL(blob);
      setResultImageUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Encoding failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecode = async () => {
    if (!coverImage) return;
    setIsProcessing(true);
    setError('');

    try {
      const imageData = await loadImageFile(coverImage);
      const options: StegoOptions = {
        pattern,
        bitsPerPixel: 1,
        channels: ['R', 'G', 'B'],
        seed: pattern === 'random' ? patternPassword : undefined,
      };

      const dataBytes = decodeFromImage(imageData, options);
      const dataStr = new TextDecoder().decode(dataBytes);
      setExtractedData(dataStr);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Extraction failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setCoverImage(null);
    setResultImageUrl('');
    setExtractedData('');
    setError('');
  };

  if (resultImageUrl && mode === 'encode') {
    return (
      <div>
        <div className="success-card">
          <div className="success-header">
            <div className="success-icon">
              <Icons.Check size={28} color="white" />
            </div>
            <div className="success-text">
              <h3>Données cachées avec succès !</h3>
              <p>Vos données chiffrées sont maintenant cachées dans l'image</p>
            </div>
          </div>
          <img src={resultImageUrl} alt="Stego result" style={{ width: '100%', maxWidth: '500px', margin: '16px auto', display: 'block', borderRadius: '10px', border: '2px solid #d1d5db' }} />
        </div>

        <div className="button-grid">
          <button className="btn-primary" onClick={() => {
            const a = document.createElement('a');
            a.href = resultImageUrl;
            a.download = `stego-image-${Date.now()}.png`;
            a.click();
          }}>
            <Icons.Download size={20} />
            Télécharger l'image stéganographique
          </button>
          <button className="btn-secondary" onClick={handleReset}>
            <Icons.RotateCcw size={20} />
            Encoder une autre
          </button>
        </div>
      </div>
    );
  }

  if (extractedData && mode === 'decode') {
    return (
      <div>
        <div className="success-card">
          <div className="success-header">
            <div className="success-icon">
              <Icons.Check size={28} color="white" />
            </div>
            <div className="success-text">
              <h3>Données extraites avec succès !</h3>
              <p>Données chiffrées récupérées de l'image</p>
            </div>
          </div>
          <div style={{ marginTop: '16px', padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #d1d5db', maxHeight: '300px', overflow: 'auto' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '8px' }}>Données chiffrées (JSON) :</div>
            <pre style={{ fontFamily: 'monospace', fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {extractedData}
            </pre>
          </div>
        </div>

        <div className="button-grid">
          <button className="btn-primary" onClick={() => navigator.clipboard.writeText(extractedData)}>
            <Icons.Copy size={20} />
            Copier les données chiffrées
          </button>
          <button className="btn-secondary" onClick={handleReset}>
            <Icons.RotateCcw size={20} />
            Extraire une autre
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Workflow Warning */}
      <div className="workflow-info" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderColor: '#f59e0b' }}>
        <div className="workflow-info-icon" style={{ background: '#f59e0b' }}>
          <Icons.AlertTriangle size={20} color="white" />
        </div>
        <div className="workflow-info-content">
          <h3 style={{ color: '#78350f' }}>⚠️ IMPORTANT : Workflow requis</h3>
          <p style={{ color: '#92400e' }}>
            Vous DEVEZ d'abord chiffrer votre texte avant d'utiliser la stéganographie !
            <br /><br />
            <strong>1.</strong> Allez dans l'onglet "Chiffrer" → Entrez votre texte → Cliquez sur "Chiffrer" → Copiez le résultat JSON
            <br />
            <strong>2.</strong> Revenez ici → Collez le JSON chiffré (pas votre texte original !) → Téléversez l'image de couverture → Cliquez sur "Cacher dans l'image"
            <br /><br />
            <strong>Erreur courante :</strong> Si vous voyez "n'est pas un JSON valide", c'est que vous collez votre texte original au lieu du JSON chiffré.
            Le JSON commence par : {"{"}"ciphertext": "..."
          </p>
        </div>
      </div>

      {/* Mode Toggle */}
      <div style={{ display: 'flex', gap: '8px', padding: '6px', background: '#f3f4f6', borderRadius: '12px', width: 'fit-content', margin: '0 auto 24px', border: '1px solid #d1d5db' }}>
        <button
          onClick={() => setMode('encode')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            background: mode === 'encode' ? 'white' : 'transparent',
            color: mode === 'encode' ? '#667eea' : '#6b7280',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: mode === 'encode' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s'
          }}
        >
          Encoder (Cacher)
        </button>
        <button
          onClick={() => setMode('decode')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            background: mode === 'decode' ? 'white' : 'transparent',
            color: mode === 'decode' ? '#667eea' : '#6b7280',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: mode === 'decode' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s'
          }}
        >
          Décoder (Extraire)
        </button>
      </div>

      {mode === 'encode' && (
        <>
          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">Coller le JSON chiffré (depuis l'onglet Chiffrer)</label>
            <textarea
              className="form-textarea"
              value={encryptedData}
              onChange={(e) => setEncryptedData(e.target.value)}
              placeholder={'Collez le JSON chiffré de l\'onglet Chiffrer ici...\n\nFormat exemple :\n{\n  "ciphertext": "abc123...",\n  "salt": "xyz789...",\n  ...\n}'}
              rows={6}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">Téléverser l'image de couverture (PNG/BMP uniquement)</label>
            <label className="upload-zone">
              <div className="upload-icon">
                <Icons.Upload size={24} style={{ color: '#667eea' }} />
              </div>
              <div className="upload-text">{coverImage ? coverImage.name : 'Téléverser une image PNG ou BMP'}</div>
              <input type="file" accept="image/png,image/bmp" onChange={handleImageUpload} hidden />
            </label>

            {/* Image Capacity Display */}
            {imageCapacity && (
              <div style={{ marginTop: '12px', padding: '12px', background: '#f0f9ff', border: '1px solid #3b82f6', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#1e40af' }}>
                  <span style={{ fontSize: '16px' }}>📊</span>
                  <strong>Capacité de cette image :</strong>
                  <span style={{ color: '#2563eb', fontFamily: 'monospace', fontWeight: 600 }}>
                    {imageCapacity.kb.toFixed(2)} KB ({imageCapacity.bytes.toLocaleString()} bytes)
                  </span>
                </div>
                {encryptedData && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#1e40af' }}>
                    Taille des données : {(new TextEncoder().encode(encryptedData).length / 1024).toFixed(2)} KB
                    {new TextEncoder().encode(encryptedData).length > imageCapacity.bytes && (
                      <span style={{ color: '#dc2626', fontWeight: 600, marginLeft: '8px' }}>
                        ⚠️ Les données sont trop volumineuses pour cette image !
                      </span>
                    )}
                    {new TextEncoder().encode(encryptedData).length <= imageCapacity.bytes && (
                      <span style={{ color: '#16a34a', fontWeight: 600, marginLeft: '8px' }}>
                        ✓ Les données peuvent être cachées dans cette image
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">Motif de distribution :</label>
            <select aria-label="Motif de distribution" className="form-select" value={pattern} onChange={(e) => setPattern(e.target.value as any)}>
              <option value="sequential">Séquentiel (Simple, rapide)</option>
              <option value="random">Aléatoire (Nécessite mot de passe, plus sûr)</option>
              <option value="spread">Étalé (Distribué uniformément)</option>
            </select>
          </div>

          {pattern === 'random' && (
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Mot de passe du motif (Requis pour aléatoire) :</label>
              <input
                type="password"
                className="form-input"
                value={patternPassword}
                onChange={(e) => setPatternPassword(e.target.value)}
                placeholder="Entrez le mot de passe du motif"
              />
            </div>
          )}

          {error && <div style={{ marginBottom: '20px', padding: '16px', background: '#fee', border: '2px solid #fca5a5', borderRadius: '10px', color: '#991b1b' }}><strong>Erreur :</strong> {error}</div>}

          <button
            className="btn-primary"
            onClick={handleEncode}
            disabled={!encryptedData || !coverImage || isProcessing || (pattern === 'random' && !patternPassword)}
          >
            {isProcessing ? (
              <>
                <div className="spinner" />
                <span>Dissimulation des données dans l'image...</span>
              </>
            ) : (
              <>
                <Icons.Image size={22} />
                <span>Cacher dans l'image</span>
              </>
            )}
          </button>
        </>
      )}

      {mode === 'decode' && (
        <>
          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">Téléverser l'image stéganographique</label>
            <label className="upload-zone">
              <div className="upload-icon">
                <Icons.Upload size={24} style={{ color: '#667eea' }} />
              </div>
              <div className="upload-text">{coverImage ? coverImage.name : 'Téléverser l\'image stégo (PNG/BMP)'}</div>
              <input type="file" accept="image/png,image/bmp" onChange={handleImageUpload} hidden />
            </label>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">Motif de distribution (Doit correspondre à l'encodage) :</label>
            <select aria-label="Motif de distribution (décodage)" className="form-select" value={pattern} onChange={(e) => setPattern(e.target.value as any)}>
              <option value="sequential">Séquentiel</option>
              <option value="random">Aléatoire (nécessite mot de passe)</option>
              <option value="spread">Étalé</option>
            </select>
          </div>

          {pattern === 'random' && (
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Mot de passe du motif :</label>
              <input
                type="password"
                className="form-input"
                value={patternPassword}
                onChange={(e) => setPatternPassword(e.target.value)}
                placeholder="Entrez le mot de passe utilisé lors de l'encodage"
              />
            </div>
          )}

          {error && <div style={{ marginBottom: '20px', padding: '16px', background: '#fee', border: '2px solid #fca5a5', borderRadius: '10px', color: '#991b1b' }}><strong>Erreur :</strong> {error}</div>}

          <button
            className="btn-primary"
            onClick={handleDecode}
            disabled={!coverImage || isProcessing || (pattern === 'random' && !patternPassword)}
          >
            {isProcessing ? (
              <>
                <div className="spinner" />
                <span>Extraction des données...</span>
              </>
            ) : (
              <>
                <Icons.Search size={22} />
                <span>Extraire les données cachées</span>
              </>
            )}
          </button>
        </>
      )}
    </div>
  );
}
